import { useAutoFocus } from '@ipfabric/ui-components/hooks/useAutoFocus';
import {
  ANY_ENTER,
  ARROW_DOWN,
  ARROW_UP,
  ESCAPE,
  SPACE_CODE,
  TAB,
} from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import { useOptionallyControlledState } from '@ipfabric/ui-components/utils/react/useOptionallyControlledState';
import { type FocusEvent, useState } from 'react';
import type { ChangeEvent, KeyboardEvent, MouseEvent, RefObject } from 'react';
import { useThrottledCallback } from 'utils/hooks/useThrottledCallback';

export type SelectSearchablePassthruProps = {
  disabled?: boolean;
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
  onBlur?: () => void;
  searchable?: boolean;
  throttle?: number;
};

type Props = SelectSearchablePassthruProps & {
  focusContainerRef?: RefObject<HTMLElement>;
};

type SelectSearchableHook = {
  blur: () => void;
  focused: boolean;
  menuOpen: boolean;
  handleChevronClick: (e: MouseEvent) => void;
  handleControlClick: (e: MouseEvent) => void;
  handleControlFocus: (e: FocusEvent) => void;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSearchKeyDown: (e: KeyboardEvent) => void;
  handleControlKeyDown: (e: KeyboardEvent) => void;
  searchInputRef: RefObject<HTMLInputElement>;
  searchValue: string;
  setFocused: (value: boolean) => void;
  setMenuOpen: (value: boolean) => void;
  setSearchValue: (value: string) => void;
};

export const useSelectSearchable = (props: Props): SelectSearchableHook => {
  const [focused, setFocusedOriginal] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const setFocused = useThrottledCallback(setFocusedOriginal, props.throttle);

  const searchInputRef = useAutoFocus(focused);

  const [searchValue, setSearchValue] = useOptionallyControlledState(
    props.inputValue,
    props.onInputValueChange,
    '',
  );

  const blur = () => {
    if (focused) {
      setFocused(false);
      setSearchValue('');

      searchInputRef.current?.blur();
      props.onBlur?.();
    }

    setMenuOpen(false);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (focused) {
      setMenuOpen(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if ([...ANY_ENTER, ARROW_DOWN, ARROW_UP, SPACE_CODE].includes(e.code)) {
      e.stopPropagation();
      e.preventDefault();
      setMenuOpen(true);
    }

    if (e.code === ESCAPE) {
      e.stopPropagation();
      e.preventDefault();
      setSearchValue('');
      setMenuOpen(false);
      (props.focusContainerRef ?? searchInputRef).current?.focus();
    }

    if (e.code === TAB) {
      // default intentionally not prevented so form navigation still works
      blur();
    }
  };

  const handleSearchKeyDown = (e: KeyboardEvent) => {
    handleKeyDown(e);
  };

  const handleControlKeyDown = (e: KeyboardEvent) => {
    handleKeyDown(e);
  };

  const handleChevronClick = (e: MouseEvent) => {
    e.stopPropagation();

    if (!props.disabled) {
      if (props.searchable) {
        // useful when component already has focus, and we click e.g. edges of the control area,
        // but not sufficient because MultiSelect renders the input only when focused so ref may
        // not yet be available. That's why the focus is also triggered by useEffect hook above.
        searchInputRef.current?.focus();
      }

      setFocused(true);
      setMenuOpen((prevState) => !prevState);
    }
  };

  const handleControlClick = (e: MouseEvent) => {
    e.stopPropagation();

    if (!props.disabled) {
      if (props.searchable) {
        // useful when component already has focus, and we click e.g. edges of the control area,
        // but not sufficient because MultiSelect renders the input only when focused so ref may
        // not yet be available. That's why the focus is also triggered by useEffect hook above.
        searchInputRef.current?.focus();
      }

      setFocused(true);
      setMenuOpen((prevState) => (!props.searchable ? !prevState : true));
    }
  };

  const handleControlFocus = (e: FocusEvent) => {
    // There is no way to tell if click or focus event happens first.
    // In order to keep option to close menu on click, we have to delay
    // focus update here
    setTimeout(() => {
      if (e.target === document.activeElement) {
        setFocused(true);
      }
    }, 20);
  };

  return {
    blur,
    focused,
    menuOpen,
    handleChevronClick,
    handleControlClick,
    handleControlFocus,
    handleSearchChange,
    handleSearchKeyDown,
    handleControlKeyDown,
    searchInputRef,
    searchValue,
    setFocused,
    setMenuOpen,
    setSearchValue,
  };
};
